// Company profile data — read straight from Supabase, never derived.
//
// Everything here comes from a table that a sync function filled from an SEC
// filing, and every row carries the URL of the filing it came from. Nothing on
// this path computes a value the company did not report, and nothing falls
// back to a static table: a missing answer is returned as null and the
// interface renders an empty state.

import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

// ── The transaction-code table ───────────────────────────────────────────────
// The SEC's own meanings, translated. This is a DISPLAY table, not a filter:
// codes are stored raw and a code missing from this list renders as its bare
// letter rather than being dropped or guessed at. Eight distinct codes turned
// up in a two-week sample of eight companies, so the list being incomplete is
// the expected state, not an edge case.
//
// The wording describes the action and stops there. What an insider meant by a
// trade is not knowable from a form 4, and the app does not speculate.
export const TRANSACTION_CODES = {
  P: { he: "רכישה בשוק",            note: "רכישה בשוק הפתוח" },
  S: { he: "מכירה",                 note: "מכירה בשוק הפתוח" },
  A: { he: "הענקה",                 note: "הענקה מהחברה · מניות או יחידות" },
  M: { he: "מימוש אופציה",          note: "המרה או מימוש של נייר נגזר" },
  F: { he: "מסירת מניות למס",       note: "מניות שנמסרו לחברה לכיסוי חבות מס" },
  G: { he: "מתנה",                  note: "העברה ללא תמורה" },
  C: { he: "המרה",                  note: "המרת נייר נגזר" },
  J: { he: "עסקה אחרת",             note: "עסקה שהמדווח סיווג כאחרת · הפירוט בדיווח" },
  D: { he: "מסירה לחברה",           note: "מסירת ניירות לחברה" },
  X: { he: "מימוש אופציה שבכסף", note: "מימוש נגזר שמחיר המימוש שלו נמוך מהמחיר בשוק או שווה לו" },
  V: { he: "עסקה בדיווח מוקדם",     note: "עסקה שדווחה מרצון לפני המועד" },
  I: { he: "עסקה שאינה בשיקול המדווח", note: "עסקה שבוצעה בידי גורם אחר" },
  K: { he: "עסקה בחוזה או ביחידות",  note: "עסקה בחוזה, יחידה או מדד ניירות" },
  L: { he: "רכישת שברי מניות",      note: "רכישה קטנה מכוח תוכנית" },
  U: { he: "עסקה בעקבות הצעת רכש",  note: "פדיון ניירות בהצעת רכש" },
  W: { he: "העברה בירושה",          note: "רכישה או העברה בירושה" },
  Z: { he: "העברה מנאמנות",         note: "הפקדה או משיכה מנאמנות הצבעה" },
};

export function describeCode(code) {
  const c = TRANSACTION_CODES[code];
  return c ? c.he : `קוד ${code}`;
}
export function codeNote(code) {
  return TRANSACTION_CODES[code]?.note || "קוד עסקה כפי שדווח · אין לנו פענוח לקוד זה";
}

// ── One shared fetch per symbol ──────────────────────────────────────────────
// The four tabs all hang off one company row, so they are loaded together in a
// single round trip rather than four. `null` means "still loading"; a loaded
// company with no data for a tab carries an empty array, which is what tells
// the tab to draw its empty state instead of a spinner.
const cache = new Map();

async function loadProfile(symbol) {
  if (!supabase) return { company: null, business: null, segments: [], officers: [], transactions: [] };

  const { data: company, error: cErr } = await supabase
    .from("companies")
    .select("cik, ticker, name, exchange, sic, fiscal_year_end, source_url, last_synced_at")
    .eq("ticker", symbol)
    .maybeSingle();

  // A company outside the covered universe is not an error — it is simply not
  // one of the companies this data set carries, and every tab says so.
  if (cErr || !company?.cik) {
    return { company: null, business: null, segments: [], officers: [], transactions: [] };
  }

  const [business, segments, officers, transactions] = await Promise.all([
    supabase.from("company_business")
      .select("description_en, description_he, source_form, source_url, filed_date, fiscal_year")
      .eq("cik", company.cik).maybeSingle(),
    supabase.from("company_segments")
      .select("segment_type, segment_name, fiscal_year, period_end, revenue, operating_income, currency, source_url, filed_at, accession_number")
      .eq("cik", company.cik),
    supabase.from("company_officers")
      .select("owner_cik, name, title, is_officer, is_director, is_ten_percent_owner, first_seen_date, last_seen_date, source_form, source_url")
      .eq("cik", company.cik),
    supabase.from("insider_transactions")
      .select("owner_name, owner_title, transaction_date, transaction_code, shares, price_per_share, shares_owned_after, acquired_disposed, security_title, source_url, filed_date")
      .eq("cik", company.cik)
      .order("transaction_date", { ascending: false })
      .limit(TRANSACTIONS_LIMIT),
  ]);

  return {
    company,
    business: business.data || null,
    segments: segments.data || [],
    officers: officers.data || [],
    transactions: transactions.data || [],
  };
}

export function useCompanyProfileData(symbol) {
  const [state, setState] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!symbol) { setState({ company: null, business: null, segments: [], officers: [], transactions: [] }); return; }
    setState(null);

    const key = String(symbol).toUpperCase();
    const hit = cache.get(key);
    if (hit) { setState(hit); return; }

    loadProfile(key)
      .then(r => { if (alive) { cache.set(key, r); setState(r); } })
      // A failed read is an empty profile, never a fabricated one.
      .catch(() => { if (alive) setState({ company: null, business: null, segments: [], officers: [], transactions: [] }); });

    return () => { alive = false; };
  }, [symbol]);

  return state;
}

// ── Segments, shaped for the "where the money comes from" tab ────────────────
/**
 * Group one breakdown type by segment, newest fiscal year first, with the
 * previous year alongside it where the filing reported one.
 *
 * ─── What is deliberately NOT computed ───────────────────────────────────────
 * No share-of-total percentage. Several companies report a sub-total AND its
 * components on the same axis — JPMorgan files "Total International" beside
 * EMEA, Asia-Pacific and Latin America — so adding the rows up double-counts
 * and any percentage derived from that sum would be wrong. Bars are therefore
 * scaled against the LARGEST ROW, which is a true comparison between rows and
 * makes no claim about a whole.
 */
export function groupSegments(segments, type) {
  const rows = segments.filter(s => s.segment_type === type && s.revenue != null);
  if (!rows.length) return { years: [], items: [], max: 0 };

  const years = [...new Set(rows.map(r => r.fiscal_year))].sort((a, b) => b - a);
  const latest = years[0];
  const prior  = years[1] ?? null;

  const byName = new Map();
  for (const r of rows) {
    if (!byName.has(r.segment_name)) byName.set(r.segment_name, {});
    byName.get(r.segment_name)[r.fiscal_year] = r;
  }

  const items = [];
  for (const [name, byYear] of byName) {
    const now = byYear[latest];
    if (!now) continue;                       // not reported in the latest year
    const was = prior != null ? byYear[prior] : null;
    // Growth only when both years exist and the base is a real positive
    // number. A percentage against zero or a negative base is meaningless,
    // and printing one would be an invented figure.
    const growth = was && was.revenue > 0
      ? ((now.revenue - was.revenue) / was.revenue) * 100
      : null;
    items.push({
      name,
      revenue: now.revenue,
      operating_income: now.operating_income,
      priorRevenue: was ? was.revenue : null,
      growth,
      currency: now.currency || "USD",
      period_end: now.period_end,
    });
  }

  items.sort((a, b) => b.revenue - a.revenue);
  return { years: [latest, prior].filter(y => y != null), items, max: items.length ? items[0].revenue : 0 };
}

/** The one source line a tab shows: which filing, filed when, with its link. */
export function segmentSource(segments) {
  const withSource = segments.find(s => s.source_url);
  if (!withSource) return null;
  return { url: withSource.source_url, filed: withSource.filed_at, accession: withSource.accession_number };
}

// ── Insider transactions, readable (sprint 14, 4.3) ─────────────────────────
// SEC form 4 gives the reporting owner as rptOwnerName, family name first and
// often in capitals ("NEWSTEAD JENNIFER"). Shown as a person is addressed:
// "Jennifer Newstead". An owner that is an entity (a fund, a trust, a
// company) keeps its name as filed. "Family, Given" is turned the same way.
const ENTITY = /\b(INC|LLC|L\.L\.C|LP|L\.P|LLP|GP|LTD|CORP|CORPORATION|CO|COMPANY|TRUST|FUND|FUNDS|GROUP|HOLDINGS?|CAPITAL|PARTNERS|PARTNERSHIP|PLC|BANK|FOUNDATION|MANAGEMENT|ADVISORS?|ASSOCIATES|INVESTMENTS?|INVESTORS|VENTURES|ENTERPRISES|ESTATE|SA|AG|NV|BV)\b\.?/i;
// Not "V": in "SMITH JOHN V" it is a middle initial (review).
const SUFFIX = /^(JR|SR|II|III|IV)\.?$/i;
// A family name that opens with one of these takes the next word with it:
// "DE LA CRUZ MARIA" is Maria De La Cruz, "VAN HOLLEN CHRIS" is Chris Van
// Hollen (review).
// Not LE, DU, DA or DI: they are also family names on their own, and
// "LE MINH ANH" is Minh Anh Le (review).
const PARTICLE = /^(DE|DEL|DELLA|DES|DOS|LA|VAN|VON|DER|DEN|TER|ST\.?|SAINT|MAC|AL|EL|BEN|BIN)$/i;
const titleWord = w => (w === w.toUpperCase() && /[A-Z]{2}/.test(w)
  ? w.toLowerCase().replace(/(^|[-'’])([a-z])/g, (m, p, c) => p + c.toUpperCase())
  : w);

export function displayName(raw) {
  const name = String(raw || "").trim().replace(/\s+/g, " ");
  if (!name) return "";
  if (ENTITY.test(name)) return name;
  let family, given, suffix = null;
  if (name.includes(",")) {
    // "Family, Given" · and "Family, Given, Jr." keeps its suffix (review).
    const [f, g, ...rest] = name.split(",").map(x => x.trim());
    if (!g) return name.split(" ").map(titleWord).join(" ");
    family = f.split(" ");
    given = g.split(" ");
    const tail = rest.join(" ").trim();
    if (tail && SUFFIX.test(tail)) suffix = tail;
    else if (tail) given.push(...tail.split(" "));
  } else {
    const parts = name.split(" ");
    if (parts.length < 2) return titleWord(name);
    let i = 0;
    while (i < parts.length - 2 && PARTICLE.test(parts[i])) i += 1;
    family = parts.slice(0, i + 1);
    given = parts.slice(i + 1);
  }
  if (!suffix && given.length > 1 && SUFFIX.test(given[given.length - 1])) suffix = given.pop();
  return [...given, ...family, ...(suffix ? [suffix] : [])].map(titleWord).join(" ");
}

// Common officer titles in Hebrew. The filer's own words stay available (the
// caller puts them in a tooltip). A title is translated only when every part
// of it is on this list; otherwise it is shown as filed, so nothing is half
// translated and nothing is guessed.
const ROLE_HE = [
  [/^(ceo|chief executive officer)$/i, 'מנכ"ל'],
  [/^(cfo|chief financial officer)$/i, 'סמנכ"ל כספים'],
  [/^(coo|chief operating officer)$/i, 'סמנכ"ל תפעול'],
  [/^(cto|chief technology officer)$/i, 'סמנכ"ל טכנולוגיות'],
  [/^(cao|chief accounting officer|principal accounting officer)$/i, "חשב החברה"],
  [/^(general counsel|chief legal officer|clo)$/i, "יועץ משפטי ראשי"],
  [/^(svp|senior vice president|sr\.? vice president|sr\.? vp)$/i, "סגן נשיא בכיר"],
  [/^(vp|vice president)$/i, "סגן נשיא"],
  [/^president$/i, "נשיא"],
  [/^(chairman|chair|chairman of the board|chairperson|board chair)$/i, 'יו"ר הדירקטוריון'],
  [/^director$/i, "דירקטור"],
  [/^(secretary|corporate secretary)$/i, "מזכיר החברה"],
  [/^treasurer$/i, "גזבר"],
  // Not "בעל עניין": in Israeli law that term starts at 5% (review).
  [/^10% owner$/i, "בעל מניות מעל 10%"],
];
export function titleHe(raw) {
  const t = String(raw || "").trim();
  if (!t) return null;
  const parts = t.split(/\s*(?:,|;|\/|&|\band\b)\s*/i).map(p => p.trim()).filter(Boolean);
  const he = parts.map(p => ROLE_HE.find(([re]) => re.test(p))?.[1] || null);
  return he.every(Boolean) ? [...new Set(he)].join(" · ") : null;
}

// How many transactions of each kind in the last `days` days, most first.
// Counted by the transaction date the filing states.
const PLURAL = {
  S: ["מכירה", "מכירות"], P: ["רכישה בשוק", "רכישות בשוק"], A: ["הענקה", "הענקות"],
  M: ["מימוש אופציה", "מימושי אופציה"], F: ["מסירת מניות למס", "מסירות מניות למס"],
  G: ["מתנה", "מתנות"], C: ["המרה", "המרות"], D: ["מסירה לחברה", "מסירות לחברה"],
  X: ["מימוש אופציה שבכסף", "מימושי אופציה שבכסף"], J: ["עסקה אחרת", "עסקאות אחרות"],
};
// The profile reads the latest TRANSACTIONS_LIMIT rows. When they all fall
// inside the window, the window may hold more than were read: the count is
// then a floor, and the screen says "לפחות" (review). Each row of a filing is
// counted: one form 4 that lists a sale at three prices is three rows.
export const TRANSACTIONS_LIMIT = 200;
export function recentIsPartial(transactions = [], { days = 90, now = Date.now() } = {}) {
  if (transactions.length < TRANSACTIONS_LIMIT) return false;
  const oldest = Math.min(...transactions.map(t => Date.parse(`${String(t.transaction_date || "").slice(0, 10)}T12:00:00Z`)).filter(Number.isFinite));
  return oldest >= now - days * 86400000;
}

export function recentByKind(transactions = [], { days = 90, now = Date.now() } = {}) {
  const since = now - days * 86400000;
  const counts = new Map();
  for (const t of transactions) {
    const at = Date.parse(`${String(t.transaction_date || "").slice(0, 10)}T12:00:00Z`);
    if (!(at >= since) || at > now + 86400000) continue;
    counts.set(t.transaction_code, (counts.get(t.transaction_code) || 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([code, count]) => {
      const p = PLURAL[code];
      return { code, count, label: p ? p[count === 1 ? 0 : 1] : describeCode(code) };
    });
}
